import { SocketClient } from "cbcore-ts"
import { IF, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHRequiredDocument } from "../../../../models/ProcurementModels"
import { RHProcurementPart, RHProcurementPartOffer } from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHFileUploadView, UploadedFile } from "../../Custom components/RH/RHFileUploadView"
import { RHOfferFileUploadView } from "../../Custom components/RH/RHOfferFileUploadView"
import { RHTextField } from "../../Custom components/RH/RHTextField"
import { RHButtons } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementPartDisplay } from "../../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHMakeOfferViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHRichTextView } from "../../Custom components/RH/RHRichTextView"


export type RHOfferItemFormSessionSnapshot = {
    partId: string
    priceText: string
    uploadedFiles: UploadedFile[]
    requiredDocumentFiles: Record<string, UploadedFile[]>
}


type PartOfferExistingFileState = {
    generalFileIds: string[]
    requiredDocumentFileIds: Record<string, string[]>
    inlineFilesById: Map<string, UploadedFile>
    allFileIds: string[]
}


/**
 * A single offer card for one procurement part.
 * Collects price, optional file attachments, and required document uploads.
 *
 * Note: offererComments is intentionally not collected here. The field is
 * part of the model and is preserved during the edit/rescind flow, but the
 * UI does not expose a comment input at this stage by design.
 *
 * Has no save/cancel buttons — saving is triggered externally by the parent
 * when the user proceeds to the preview step.
 */
export class EnterProcurementItemOfferView extends UIView {
    
    // ── Callbacks ─────────────────────────────────────────────────────────────
    
    offerItemDidChange?: () => void
    didRequestDelete?: () => void
    
    // ── Subviews ──────────────────────────────────────────────────────────────
    
    readonly priceLabel = new UITextView()
        .configuredWithObject({
            text: "Pakkumuse koondmaksumus (KM-ta):",
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: "18px", fontWeight: "700", color: RHColor.dark.stringValue }
        })
        .addedAsSubviewToView(this)
    
    readonly priceField = new RHTextField()
        .configuredWithObject({
            placeholderText: "0,00",
            type: "number",
            allowDecimals: YES,
            usesCommaAsDecimalSeparator: YES,
            minValue: 0,
            thousandsSeparator: "\u00A0",
            showsFormattedDisplayWhileFocused: YES
        }).addedAsSubviewToView(this)
    
    readonly currencyLabel = new UITextView()
        .configuredWithObject({
            text: "€",
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: "18px", fontWeight: "500", color: RHColor.dark.stringValue }
        })
        .addedAsSubviewToView(this)
    
    readonly deleteButton = new RHButton()
        .configuredWithObject({
            hoverText: "Eemalda pakkumus",
            titleLabel: {
                text: RHButtons.deleteIconHTML,
                style: {
                    fontFamily: "Arial, sans-serif",
                    fontSize: "1.35rem",
                    fontWeight: "400",
                    lineHeight: "1"
                }
            },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: 0,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.didRequestDelete?.()
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly offerDocumentsLabel = new UITextView()
        .configuredWithObject({
            text: "Pakkumuse dokumendid (valikuline)",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "16px",
                fontWeight: "600",
                color: RHColor.textPrimary.stringValue
            }
        })
        .addedAsSubviewToView(this)
    
    readonly fileUploadView = new RHOfferFileUploadView()
        .configuredWithObject({
            variant: RHFileUploadView.variant.full,
            bulkDownloadEnabled: NO,
            // Offer file references change on save so cancelling preserves the stored files.
            shouldDeleteFilesWhenRemovedFromList: NO,
            allowedFileTypes: RHFileUploadView.procurementOfferAllowedFileTypes
        })
        .addedAsSubviewToView(this)
    
    // ── Document requirement slots ────────────────────────────────────────────
    // Current and former requirements; former slots retain their pending uploads.
    
    _documentRequirementSlots: Array<{
        document: RHRequiredDocument
        isRequired: boolean
        nameLabel: UITextView
        descriptionLabel: RHRichTextView
        uploadView: RHFileUploadView
    }> = []
    
    // ── State ─────────────────────────────────────────────────────────────────
    
    private _part?: RHProcurementPart
    private _isEditable = true
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = [this.priceField, this.fileUploadView.dropZone]
        for (const slot of this._documentRequirementSlots) {
            if (slot.uploadView.superview === this) {
                items.push(slot.uploadView.dropZone)
            }
        }
        return items
    })
    
    constructor(elementID?: string) {
        super(elementID)
        ;(this as EnterProcurementItemOfferView).configureWithObject({
            style: {
                border: `1px solid ${RHColor.borderLight.stringValue}`,
                borderRadius: "8px",
                backgroundColor: UIColor.whiteColor.stringValue,
                padding: "0px"
            }
        })
        
        
        this.priceField.addTargetForControlEvent(RHTextField.controlEvent.TextChange, () => {
            this.offerItemDidChange?.()
        })
        
        this.fileUploadView.setOnFilesChanged(() => {
            this.offerItemDidChange?.()
        })
    }
    
    // ── Public API ────────────────────────────────────────────────────────────
    
    get partId(): string | undefined {
        return this._part?._id?.toString()
    }
    
    get part(): RHProcurementPart | undefined {
        return this._part
    }
    
    /** True if the user has entered a valid price (card is worth including in a save). */
    get hasPrice(): boolean {
        return Number.isFinite(this.priceField.numericValue)
    }
    
    /** The current price as a number, or undefined if the field is empty or invalid. */
    get priceValue(): number | undefined {
        const priceValue = this.priceField.numericValue
        if (!Number.isFinite(priceValue) || priceValue < 0) {
            return undefined
        }
        return priceValue
    }
    
    /** Uploaded file IDs outside the current required-document mappings. */
    get optionalFileIds(): string[] {
        return this._uploadedFilesForOptionalDocuments().map(uploadedFile => uploadedFile.id)
    }
    
    /**
     * Map of required document slot ID → all successfully uploaded file IDs.
     */
    get requiredDocumentFileIds(): Record<string, string[]> {
        const result: Record<string, string[]> = {}
        for (const slot of this._documentRequirementSlots) {
            if (!slot.isRequired) {
                continue
            }
            const uploadedFiles = slot.uploadView.uploadedFiles
            if (uploadedFiles.length > 0) {
                result[slot.document._id] = uploadedFiles.map(uploadedFile => uploadedFile.id)
            }
        }
        return result
    }


    sessionSnapshot(): RHOfferItemFormSessionSnapshot {
        const requiredDocumentFiles: Record<string, UploadedFile[]> = {}
        for (const slot of this._documentRequirementSlots) {
            if (!slot.isRequired) {
                continue
            }
            requiredDocumentFiles[slot.document._id] = slot.uploadView.uploadedFiles.map(
                uploadedFile => ({ ...uploadedFile })
            )
        }
        return {
            partId: this.partId ?? "",
            priceText: this.priceField.text,
            uploadedFiles: this._uploadedFilesForOptionalDocuments().map(uploadedFile => ({
                ...uploadedFile
            })),
            requiredDocumentFiles
        }
    }


    restoreSessionSnapshot(snapshot: RHOfferItemFormSessionSnapshot) {
        if (snapshot.partId !== this.partId) {
            return
        }
        this.priceField.text = snapshot.priceText
        this.fileUploadView.clearFiles()
        if (snapshot.uploadedFiles.length > 0) {
            this.fileUploadView.initWithExistingFiles(snapshot.uploadedFiles)
        }
        for (const slot of this._documentRequirementSlots) {
            const uploadedFiles = snapshot.requiredDocumentFiles[slot.document._id] ?? []
            slot.uploadView.clearFiles()
            if (uploadedFiles.length > 0) {
                slot.uploadView.initWithExistingFiles(uploadedFiles)
            }
        }
    }
    
    /**
     * Must be called once procurementId is known (before the user uploads files).
     * offerId is optional — omit it for new offers that haven't been saved yet.
     * Once the offer is saved, call this again with the real offerId.
     */
    configureForOffer(params: { procurementId: string; offerId?: string }): void {
        this.fileUploadView.configureForOffer(params)
    }

    /**
     * Configure the card for a specific part, optionally pre-populating it
     * from a saved part offer (e.g. when loading an existing draft).
     */
    async configureForPart(
        part: RHProcurementPart,
        savedPartOffer?: RHProcurementPartOffer,
        isEditable = YES,
        numberOfParts = 1
    ) {
        this._part = part
        this._isEditable = isEditable

        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(numberOfParts)) {
            this.priceLabel.text = "Osa maksumus (KM-ta):"
        }
        else {
            this.priceLabel.text = "Pakkumuse koondmaksumus (KM-ta):"
        }
        
        this._rebuildSlotsForRequiredDocuments(part.requiredDocuments ?? [])
        
        if (savedPartOffer) {
            await this._populateFromPartOffer(savedPartOffer)
        }
        else {
            this._resetForm()
        }
        
        this._updateEditability()
    }

    updateRequiredDocuments(documents: RHRequiredDocument[]) {
        if (!this._part) {
            return
        }
        this._part = { ...this._part, requiredDocuments: documents }
        const previousSlots = this._documentRequirementSlots
        this._documentRequirementSlots = documents.map(document => {
            const slot = previousSlots.find(candidate => candidate.document._id === document._id) ??
                this._createSlotForRequiredDocument(document)
            slot.document = document
            slot.isRequired = YES
            slot.nameLabel.text = RHTextSanitizer.escapedHTML(`Nõutud dokument: ${document.name}`)
            slot.descriptionLabel.configureWithText(document.description ?? "", document.descriptionFormat)
            slot.descriptionLabel.removeFromSuperview()
            if (document.description) {
                slot.descriptionLabel.addedAsSubviewToView(this)
            }
            return slot
        })
        for (const slot of previousSlots) {
            if (this._documentRequirementSlots.includes(slot)) {
                continue
            }
            // Keep the upload view alive so an in-flight upload remains attached to this offer.
            slot.isRequired = NO
            slot.nameLabel.text = RHTextSanitizer.escapedHTML(`Dokument ei ole enam nõutud: ${slot.document.name}`)
            this._documentRequirementSlots.push(slot)
        }
        this._updateEditability()
        this.setNeedsLayoutUpToRootView()
    }

    _uploadedFilesForOptionalDocuments(): UploadedFile[] {
        const filesById = new Map<string, UploadedFile>()
        for (const file of this.fileUploadView.uploadedFiles) {
            filesById.set(file.id, file)
        }
        for (const slot of this._documentRequirementSlots) {
            if (!slot.isRequired) {
                for (const file of slot.uploadView.uploadedFiles) {
                    filesById.set(file.id, file)
                }
            }
        }
        return [...filesById.values()]
    }
    
    // ── Private helpers ───────────────────────────────────────────────────────
    
    _rebuildSlotsForRequiredDocuments(documents: RHRequiredDocument[]) {
        // Remove old slots from the view tree
        this._documentRequirementSlots.forEach(slot => {
            slot.nameLabel.removeFromSuperview()
            slot.descriptionLabel.removeFromSuperview()
            slot.uploadView.removeFromSuperview()
        })
        this._documentRequirementSlots = []
        
        this._documentRequirementSlots = documents.map(document => this._createSlotForRequiredDocument(document))
    }

    _createSlotForRequiredDocument(document: RHRequiredDocument): EnterProcurementItemOfferView["_documentRequirementSlots"][number] {
        const nameLabel = new UITextView()
            .configuredWithObject({
                text: RHTextSanitizer.escapedHTML(`Nõutud dokument: ${document.name}`),
                style: {
                    fontSize: "16px",
                    fontWeight: "600",
                    color: RHColor.textPrimary.stringValue
                }
            })
            .addedAsSubviewToView(this)

        const descriptionLabel = new RHRichTextView()
            .configuredWithObject({
                style: {
                    fontSize: "13px",
                    color: RHColor.textSecondary.stringValue
                }
            })

        descriptionLabel.configureWithText(document.description ?? "", document.descriptionFormat)

        if (document.description) {
            descriptionLabel.addedAsSubviewToView(this)
        }

        const uploadView = new RHFileUploadView()
            .configuredWithObject({
                variant: RHFileUploadView.variant.full,
                bulkDownloadEnabled: NO,
                shouldDeleteFilesWhenRemovedFromList: NO,
                allowedFileTypes: RHFileUploadView.procurementOfferAllowedFileTypes
            })
            .addedAsSubviewToView(this)
        uploadView.setOnFilesChanged(() => {
            this.offerItemDidChange?.()
        })

        return { document, isRequired: YES, nameLabel, descriptionLabel, uploadView }
    }

    private async _populateFromPartOffer(partOffer: RHProcurementPartOffer) {
        this.priceField.text = partOffer.price.amount.toString()

        this.fileUploadView.clearFiles()
        this._documentRequirementSlots.forEach(slot => slot.uploadView.clearFiles())

        const existingFileState = EnterProcurementItemOfferView._stateForPartOffer(partOffer)
        const resolvedFilesById = new Map(existingFileState.inlineFilesById)
        const unresolvedFileIds = existingFileState.allFileIds.filter(
            fileId => !resolvedFilesById.has(fileId)
        )

        if (unresolvedFileIds.length > 0) {
            const companyId = await RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyIdAfterLoadingAffiliations()
            const response = await SocketClient.RetrieveFilesMetadata({
                fileIds: unresolvedFileIds,
                companyId
            })
            for (const file of response.result ?? []) {
                resolvedFilesById.set(file.id, file)
            }
        }

        const filesStillMissing = existingFileState.allFileIds.filter(
            fileId => !resolvedFilesById.has(fileId)
        )
        if (filesStillMissing.length > 0) {
            throw new Error("One or more existing offer files could not be loaded.")
        }

        const generalFiles = existingFileState.generalFileIds
            .map(fileId => resolvedFilesById.get(fileId))
            .filter((file): file is UploadedFile => !!file)
        if (generalFiles.length > 0) {
            this.fileUploadView.initWithExistingFiles(generalFiles)
        }

        for (const slot of this._documentRequirementSlots) {
            const files = (existingFileState.requiredDocumentFileIds[slot.document._id] ?? [])
                .map(fileId => resolvedFilesById.get(fileId))
                .filter((file): file is UploadedFile => !!file)
            if (files.length > 0) {
                slot.uploadView.initWithExistingFiles(files)
            }
        }
    }
    
    _resetForm() {
        this.priceField.text = ""
        this.fileUploadView.clearFiles()
        this._documentRequirementSlots.forEach(slot => slot.uploadView.clearFiles())
    }
    
    _updateEditability() {
        this.priceField.enabled = this._isEditable
        this.fileUploadView.enabled = this._isEditable
        this._documentRequirementSlots.forEach(slot => {
            slot.uploadView.enabled = this._isEditable
        })
        if (this._isEditable) {
            this.deleteButton.addedAsSubviewToView(this)
        }
        else {
            this.deleteButton.removeFromSuperview()
        }
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        const labelHeight = padding * 2
        
        // Row 1: price label — price field — "€" — delete button (if editable)
        const priceRow = bounds.rectangleWithHeight(labelHeight)
        const priceRowViews: UIView[] = IF(this.deleteButton.superview === this)(() =>
            [this.priceLabel, this.priceField, this.currencyLabel, this.deleteButton] as UIView[]
        ).ELSE(() =>
            [this.priceLabel, this.priceField, this.currencyLabel] as UIView[]
        )
        const priceRowAbsoluteWidths: number[] = IF(this.deleteButton.superview === this)(() =>
            [
                this.priceLabel.intrinsicContentWidth(priceRow.height) + padding,
                RHMakeOfferViewTokens.offerItemPriceFieldWidth,
                RHMakeOfferViewTokens.offerItemCurrencyLabelWidth,
                labelHeight
            ] as number[]
        ).ELSE(() =>
            [
                this.priceLabel.intrinsicContentWidth(priceRow.height) + padding,
                RHMakeOfferViewTokens.offerItemPriceFieldWidth,
                RHMakeOfferViewTokens.offerItemCurrencyLabelWidth
            ] as number[]
        )
        priceRow.distributeViewsAlongWidth(
            priceRowViews,
            undefined,
            padding,
            priceRowAbsoluteWidths
        )
        if (this.deleteButton.superview === this) {
            this.deleteButton.frame = this.deleteButton.frame
                .rectangleWithWidth(RHMakeOfferViewTokens.deleteOfferButtonSize, 0.5)
                .rectangleWithHeight(RHMakeOfferViewTokens.deleteOfferButtonSize, 0.5)
        }
        
        // Row 2: general offer documents
        let current = priceRow
            .rectangleForNextRow(padding * 0.75, this.offerDocumentsLabel)
            .assignedAsFrameOfView(this.offerDocumentsLabel)
            .rectangleForNextRow(padding * 0.25, this.fileUploadView)
            .assignedAsFrameOfView(this.fileUploadView)
        
        // Current and former document requirement slots
        for (const slot of this._documentRequirementSlots) {
            current = current
                .rectangleForNextRow(padding, slot.nameLabel)
                .assignedAsFrameOfView(slot.nameLabel)
            
            if (slot.descriptionLabel.superview === this) {
                current = current
                    .rectangleForNextRow(padding * 0.25, slot.descriptionLabel)
                    .assignedAsFrameOfView(slot.descriptionLabel)
            }
            
            current = current
                .rectangleForNextRow(padding * 0.25, slot.uploadView)
                .assignedAsFrameOfView(slot.uploadView)
        }
    }


    static _stateForPartOffer(partOffer: RHProcurementPartOffer): PartOfferExistingFileState {
        const generalFileIds: string[] = []
        const requiredDocumentFileIds: Record<string, string[]> = {}
        const inlineFilesById = new Map<string, UploadedFile>()

        for (const reference of partOffer.files ?? []) {
            const fileId = EnterProcurementItemOfferView._fileIdFromReference(reference)
            if (!fileId) {
                continue
            }
            generalFileIds.push(fileId)
            const inlineFile = EnterProcurementItemOfferView._uploadedFileFromReference(reference)
            if (inlineFile) {
                inlineFilesById.set(fileId, inlineFile)
            }
        }

        for (const [documentId, reference] of Object.entries(
            partOffer.requiredDocumentFileIds ?? {}
        )) {
            requiredDocumentFileIds[documentId] = [reference].flat()
                .map(fileReference => EnterProcurementItemOfferView._fileIdFromReference(fileReference))
                .filter((fileId): fileId is string => !!fileId)
        }

        for (const reference of Object.values(partOffer.requiredDocumentFiles ?? {}).flat()) {
            const fileId = EnterProcurementItemOfferView._fileIdFromReference(reference)
            const inlineFile = EnterProcurementItemOfferView._uploadedFileFromReference(reference)
            if (fileId && inlineFile) {
                inlineFilesById.set(fileId, inlineFile)
            }
        }

        return {
            generalFileIds,
            requiredDocumentFileIds,
            inlineFilesById,
            allFileIds: [...new Set([
                ...generalFileIds,
                ...Object.values(requiredDocumentFileIds).flat()
            ])]
        }
    }


    // File references arrive as a plain fileId string, a populated file document
    // (Mongoose "_id"), or a locally-uploaded file (app-side "id") - this normalizes
    // any of the three down to the fileId string.
    static _fileIdFromReference(
        reference: string | { id?: unknown, _id?: unknown, toString?: () => string } | undefined
    ): string | undefined {
        if (typeof reference === "string") {
            return reference || undefined
        }
        if (!reference || typeof reference !== "object") {
            return undefined
        }

        const nestedReference = reference.id ?? reference._id
        if (nestedReference && nestedReference !== reference) {
            const nestedId = EnterProcurementItemOfferView._fileIdFromReference(
                nestedReference as string | { id?: unknown, _id?: unknown, toString?: () => string }
            )
            if (nestedId) {
                return nestedId
            }
        }

        const stringValue = reference.toString?.()
        if (stringValue && stringValue !== "[object Object]") {
            return stringValue
        }
        return undefined
    }


    static _uploadedFileFromReference(
        reference: string | { id?: unknown, _id?: unknown, toString?: () => string } | undefined
    ): UploadedFile | undefined {
        if (!reference || typeof reference !== "object") {
            return undefined
        }

        const file = reference as {
            name?: string
            type?: string
            size?: number
            compressedSize?: number
        }
        const id = EnterProcurementItemOfferView._fileIdFromReference(reference)
        if (
            !id ||
            typeof file.name !== "string" ||
            typeof file.type !== "string" ||
            typeof file.size !== "number"
        ) {
            return undefined
        }

        const uploadedFile: UploadedFile = {
            id,
            name: file.name,
            type: file.type,
            size: file.size
        }
        if (typeof file.compressedSize === "number") {
            uploadedFile.compressedSize = file.compressedSize
        }
        return uploadedFile
    }

}
